import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/admin/ApiConfig";
import {PropertyApi, type PropertyRequest, UserPropertiesDetails} from "components/src/api/admin/generated";
import {RenderAlert} from "components/src/common/RenderAlert";
import {SaveChangesRef} from "components/src/common/SaveChangesRef"
import {useAvailablePropertiesContext} from "components/src/hook/UseAvailablePropertiesContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {PageContent} from "components/src/layout/PageContent.tsx";
import {PageHeader} from "components/src/layout/PageHeader.tsx";
import {Assert} from "components/src/utils/Assert";
import {logger} from "components/src/utils/Logger";
import {Utils} from "components/src/utils/Utils";
import {Settings as SettingsIcon,} from "iconoir-react";
import {Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useImperativeHandle, useState} from 'react'
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from 'react-bootstrap/Form';
import Row from "react-bootstrap/Row";
import {useTranslation} from "react-i18next";
import {useBlocker, useNavigate} from "react-router-dom";
import {
    ExtendedUserPropertyDetails,
    PropertyValueWithUserPropertyDetails
} from "./PropertyValueWithUserPropertyDetails";
import {SettingsLevel} from "./SettingsUtils";
import {UserProperty} from "./UserProperty";
import "./SettingsView.scss";

/**
 * REST api for accessing the back-end
 */
const propertyApi = new PropertyApi(ApiConfig.config());

/**
 * level (system, domain or user) and email address for user level and domain name for a domain level
 */
interface SettingsLevelAndSubLevelProps {
    settingsLevel : SettingsLevel
    settingsLevelSubSelector : string | undefined
    // categories is a string where the categories are separated with a forward slash(/)
    // main reason for this is that a string array will always have a different identity and therefore always results
    // in a state change detection when categories is used as a dependency for effect etc.
    categories : string
    setErrorMessage : Dispatch<SetStateAction<string | undefined>>
    setShouldSave : Dispatch<SetStateAction<boolean>>
    setSaving : Dispatch<SetStateAction<boolean>>
}

/**
 *  Recursively find the userPropertiesDetails from the categories
 */
function findUserPropertiesDetails(userPropertiesDetails : UserPropertiesDetails[], categories : string[]) : UserPropertiesDetails
{
    const category = categories.shift();

    const foundUserPropertiesDetails = userPropertiesDetails.find(
        (userPropertiesDetails) => userPropertiesDetails.category.toLowerCase() === category);

    if (foundUserPropertiesDetails === undefined || foundUserPropertiesDetails == null) {
        throw new Error(`Properties object with category name ${category} does not exist`)
    }

    // if there are categories left,
    return categories.length == 0 ? foundUserPropertiesDetails : findUserPropertiesDetails(foundUserPropertiesDetails.children, categories);
}

/**
 * Show the user settings on system/domain or user level. Which level is shown is determined by the URL path segments.
 *
 * The path params have the following structure
 *
 * path :== /system/<category>  | /domain/<category>/<domain> | /user/<category>/<email>
 *
 * <category> :== <category-name> | <category-name>/<category>
 * <category-name> :== <letter> | <letter><category-name>
 *
 * For the user or domain level, the settings are shown on a modal popup. The modal popup contains a side bar menu
 * for the relevant settings for the domain or user. On smaller screens, the menu will be shown as an off canvas popup
 */
export const SettingsView = forwardRef<SaveChangesRef | undefined, SettingsLevelAndSubLevelProps>((({
                                             settingsLevel,
                                             settingsLevelSubSelector,
                                             categories,
                                             setErrorMessage,
                                             setShouldSave,
                                             setSaving} : SettingsLevelAndSubLevelProps, ref) =>
{
    /**
     * All the available properties (system, domain and user level)
     */
    const {availableProperties, availablePropertiesLoading} = useAvailablePropertiesContext();
    /**
     * The loaded user properties
     */
    const [userProperties, setUserProperties] = useState<PropertyValueWithUserPropertyDetails[]>();
    /**
     * Keeps track of the properties which are changed
     */
    const [propertiesToSet, setPropertiesToSet ] = useState<PropertiesToSet>({})
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or if the settings
     * page is closed to the users or domains page
     */
    const navigate = useNavigate();
    /**
     * Set to true to force a reload of the properties from the back-end
     */
    const [forcePropertiesReload, setForcePropertiesReload] = useState(false);
    /**
     * The name of the property for which failed validation
     */
    const [failingProperty, setFailingProperty] = useState();
    /**
     * The validation failure error message
     */
    const [validationErrorMessage, setValidationErrorMessage] = useState<string>();
    /**
     * Hide inherited settings
     */
    const [hideInherited, setHideInherited] = useState(false);
    /**
     * The key used to retrieve the display name of the page title
     */
    const [titleDisplayNameKey, setTitleDisplayNameKey] = useState<string | undefined>(undefined);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();
    const {t:tu} = useTranslation('userproperties');
    /**
     * A variable that leverages the `useBlocker` hook to prevent navigation
     * between routes when certain conditions are met, typically used to prompt
     * users about unsaved changes before they leave the current page.
     *
     * The blocking logic checks if saving is required (`shouldSave` is true) and
     * if the user attempts to navigate to a different path than the current one.
     *
     * The function passed to the `useBlocker` hook receives two arguments:
     * - `currentLocation`: The current location object representing the current route path.
     * - `nextLocation`: The location object representing the next route path being navigated to.
     */
    const unsavedChangesBlocker = useBlocker(
        ({ currentLocation, nextLocation }) =>
            Object.keys(propertiesToSet).length > 0 &&
            currentLocation.pathname !== nextLocation.pathname
    );

    /**
     * Override the forwarded ref with the saveSettings method to allow the parent to save the modified settings
     */
    useImperativeHandle(ref, () => {
        return {
            saveChanges(){
                save();
            }
        }
    });

    interface PropertiesToSet {
        [key : string] : string | undefined;
    }

    /**
     * Returns the available properties for this level
     */
    const getAvailablePropertiesAtLevel = useCallback((settingsLevel: SettingsLevel) : UserPropertiesDetails[] =>
    {
        let levelProperties: UserPropertiesDetails[];

        switch (settingsLevel) {
            case SettingsLevel.System:
                levelProperties = availableProperties.globalProperties;
                break;
            case SettingsLevel.Domain:
                levelProperties = availableProperties.domainProperties;
                break;
            case SettingsLevel.User:
                levelProperties = availableProperties.userProperties;
                break;
        }
        return levelProperties;
    }, [availableProperties.globalProperties,
        availableProperties.domainProperties,
        availableProperties.userProperties]);

    interface UserPropertiesDetailsResult {
        userPropertiesDetails : UserPropertiesDetails,
        settingsLevel : SettingsLevel,
        settingsLevelSubSelector : string | undefined;
    }

    const findUserPropertiesDetailsForPath = useCallback((): UserPropertiesDetailsResult =>
    {
        // get the available properties for the level
        const availablePropertiesAtLevel : UserPropertiesDetails[] = getAvailablePropertiesAtLevel(settingsLevel);

        // find the UserPropertiesDetails for the level and path
        const userPropertiesDetails : UserPropertiesDetails = findUserPropertiesDetails(
            availablePropertiesAtLevel,
            // note: we must clone categories because findUserPropertiesDetails will modify the array
            categories.split("/"));

        return {
            userPropertiesDetails : userPropertiesDetails,
            settingsLevel : settingsLevel,
            settingsLevelSubSelector : settingsLevelSubSelector};
    }, [categories,
        getAvailablePropertiesAtLevel,
        settingsLevel,
        settingsLevelSubSelector]);

    useEffect(() =>
    {
        // skip if the available properties are loading
        if (availablePropertiesLoading) {
            return;
        }

        // reset state
        setUserProperties(undefined);
        setPropertiesToSet({});
        setShouldSave(false);
        setFailingProperty(undefined);
        setValidationErrorMessage(undefined);
        setErrorMessage(undefined);
        setForcePropertiesReload(false);

        let userPropertiesDetailsResult : UserPropertiesDetailsResult;

        try {
            userPropertiesDetailsResult = findUserPropertiesDetailsForPath();
        }
        catch(e) {
            // this can happen if the user does not have the rights to read at least one property of the
            // general category
            setErrorMessage("Error loading user property values. Message: " + (e as Error).message);

            return;
        }

        // set the page title key
        setTitleDisplayNameKey(userPropertiesDetailsResult.userPropertiesDetails?.displayNameKey);

        const propertyNames = userPropertiesDetailsResult.userPropertiesDetails?.properties
            .map(pd => pd.getter.name) ?? [];

        let loadPropertiesCallback;

        // create a get property callback based on the settings level
        switch (userPropertiesDetailsResult.settingsLevel) {
            case SettingsLevel.System:
                loadPropertiesCallback = async () => {
                    return await propertyApi.propertyControllerGetGlobalProperties(propertyNames);
                };
                break;
            case SettingsLevel.Domain:
                loadPropertiesCallback = async () => {
                    return await propertyApi.propertyControllerGetDomainProperties(
                        Assert.assertNotUndefined(userPropertiesDetailsResult.settingsLevelSubSelector,
                            "settingsLevelSubSelector"), propertyNames);
                };
                break;
            case SettingsLevel.User:
                loadPropertiesCallback = async () => {
                    return await propertyApi.propertyControllerGetUserProperties(
                        Assert.assertNotUndefined(userPropertiesDetailsResult.settingsLevelSubSelector,
                            "settingsLevelSubSelector"), propertyNames);
                };
                break;
        }

        if (loadPropertiesCallback === undefined) {
            throw new Error("loadPropertiesCallback is undefined");
        }

        loadPropertiesCallback().then((response) =>
        {
            logger.info("Loading user properties")

            const getUserPropertyDetails = (name : string) : ExtendedUserPropertyDetails | undefined =>
            {
                const details = userPropertiesDetailsResult.userPropertiesDetails?.properties
                    .find((detail) => detail.getter.name === name);

                if (details === undefined) {
                    throw new Error(`User property with name ${name} not found`);
                }

                let readOnly : boolean;

                switch (userPropertiesDetailsResult.settingsLevel)
                {
                    case SettingsLevel.System: readOnly = !details.setter.global; break;
                    case SettingsLevel.Domain: readOnly = !details.setter.domain; break;
                    case SettingsLevel.User: readOnly = !details.setter.user; break;
                }

                return {...details.getter, readonly : readOnly};
            }

            setUserProperties(response.data.map(propertyValue => ({propertyValue : propertyValue,
                userPropertyDetails : getUserPropertyDetails(propertyValue.name)} as PropertyValueWithUserPropertyDetails)));
        }).catch((err: Error | AxiosError) => {
            logger.error("Error loading user property values", err);
            setUserProperties(undefined);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
    }, [availablePropertiesLoading,
        findUserPropertiesDetailsForPath,
        setErrorMessage,
        navigate,
        setShouldSave,
        forcePropertiesReload,
        t]);

    /**
     * Called when a user property is changed. If value is undefined, the property should be inherited
     */
    const onPropertyChange = (name : string, value : string | undefined) =>
    {
        if (value === null) {
            throw new Error(`Property ${name} is null`);
        }

        // reset error messages when input changed
        setValidationErrorMessage(undefined);
        setErrorMessage(undefined);

        // if value is the empty string, it should be treated as inherit
        value = value === "" ? undefined : value;

        // find the property with the given name
        const property = userProperties?.find(
            propertyDetails => propertyDetails.userPropertyDetails.name === name);

        if (property === undefined) {
            throw new Error(`Property ${name} not found`);
        }

        let skipProperty = false;

        // check if the property value was changed
        if (value === undefined) {
            // new value should be inherited
            // if previous value is also inherited, property is not changed
            if (property.propertyValue.inherited)
            {
                logger.debug(`Property ${name} inherit was not changed`)
                skipProperty = true;
            }
        }
        // if previous values was not inherited and the value was not changed, the property is not changed
        else if (!property.propertyValue.inherited && property.propertyValue.value === value) {
            logger.debug(`Property ${name} value was not changed`)
            skipProperty = true;
        }

        let updatedPropertiesToSet;

        if (!skipProperty)
        {
            logger.debug(`Property ${name} changed`)

            updatedPropertiesToSet = {
                ...propertiesToSet,
                [name]: value
            };
        } else {
            // if a property was previously set, it should be removed
            // since we should not modify propertiesToSet, we make a copy
            updatedPropertiesToSet = {...propertiesToSet}
            delete updatedPropertiesToSet[name];
        }

        setPropertiesToSet(updatedPropertiesToSet);

        setShouldSave(Object.keys(updatedPropertiesToSet).length > 0);
    }

    /**
     * Saves all updated settings
     */
    const save = () =>
    {
        logger.info("Saving user properties")

        const propertyRequests : PropertyRequest[] = Object.keys(propertiesToSet).map(name => {return {name : name, value: propertiesToSet[name]}});

        let savePropertiesCallback;

        // create a get property callback based on the settings level
        switch (settingsLevel) {
            case SettingsLevel.System:
                savePropertiesCallback = async () => {
                    return await propertyApi.propertyControllerSetGlobalProperties(propertyRequests);
                };
                break;
            case SettingsLevel.Domain:
                savePropertiesCallback = async () => {
                    return await propertyApi.propertyControllerSetDomainProperties(
                        Assert.assertNotUndefined(settingsLevelSubSelector,
                            "settingsLevelSubSelector"), propertyRequests);
                };
                break;
            case SettingsLevel.User:
                savePropertiesCallback = async () => {
                    return await propertyApi.propertyControllerSetUserProperties(
                        Assert.assertNotUndefined(settingsLevelSubSelector,
                            "settingsLevelSubSelector"), propertyRequests);
                };
                break;
        }

        setSaving(true);

        savePropertiesCallback().then(() =>
        {
            logger.info("User properties saved")

            // reset save state
            setPropertiesToSet({});
            setShouldSave(false);

            // force a reload of the properties
            setForcePropertiesReload(true);
        }).catch((err: Error | AxiosError) => {
            logger.error("Error saving user property values", err);
            Utils.redirectToLoginIfNotAuthenticated(err, navigate);

            // check if the error is caused by validation failure
            if (err instanceof AxiosError && err.response?.data["className"] ===
                "com.ciphermail.core.app.properties.PropertyValidationFailedException")
            {
                setFailingProperty(err.response.data["propertyName"]);
                setValidationErrorMessage(Utils.getErrorMessage(err, t));
            }
            else {
                // some other error
                setErrorMessage(Utils.getErrorMessage(err, t));
            }
        }).finally(() => {
            setSaving(false);
        })
    }

    function discardChanges()
    {
        setPropertiesToSet({});
        setShouldSave(false);

        if (unsavedChangesBlocker?.proceed) {
            unsavedChangesBlocker.proceed()
        }
    }

    const filterUserProperties = userProperties?.filter(property => !(hideInherited && property.propertyValue.inherited));

    /**
     * Renders all user properties
     */
    return (
        <div className="d-flex flex-column h-100">
            {titleDisplayNameKey !== undefined &&
                <PageHeader icon={<SettingsIcon />} title={tu(titleDisplayNameKey)}/>
            }

            <Row className="px-4">
                {unsavedChangesBlocker.state === "blocked" ? (
                    <div className="d-flex flex-column">
                        <div className="w-100">
                            <RenderAlert message={t('common.discard-unsaved-settings')}/>
                        </div>
                        <div className="d-flex flex-row gap-2">
                            <Button variant="danger" onClick={() => discardChanges()}>
                                {t('common.discard-unsaved-settings-leave')}
                            </Button>
                            <Button variant="success" onClick={() => unsavedChangesBlocker.reset()}>
                                {t('common.discard-unsaved-settings-cancel')}
                            </Button>
                        </div>
                    </div>
                ) : null}
            </Row>

            <Row className="d-inline settings-view-max-width">
                <Col>
                    <Form.Group className="float-end m-4">
                        <Form.Check
                            id="hide-inherited"
                            label={t('settings-view.hide-inherited')}
                            title={t('settings-view.hide-inherited-title')}
                            reverse
                            type="switch"
                            onChange={e => setHideInherited(e.target.checked)} />
                    </Form.Group>
                </Col>
            </Row>

            <PageContent className="flex-grow-1 min-h-0">
                <Row className="w-100">
                    <div className="row overflow-auto settings-view-max-width">
                        <div className="col">
                            {filterUserProperties?.map((property) => (
                                <UserProperty key={property.propertyValue.name}
                                              propertyDetails={{
                                                  propertyValue: property.propertyValue,
                                                  userPropertyDetails: property?.userPropertyDetails
                                              }}
                                              onPropertyChange={onPropertyChange}
                                              errorMessage={property.propertyValue.name === failingProperty ? validationErrorMessage : undefined}
                                />
                            ))}
                            {filterUserProperties?.length === 0 && <RenderAlert message={t('settings-view.no-settings-override')} variant="info"/>}
                        </div>
                    </div>
                </Row>
            </PageContent>
        </div>
    )
}))

// see https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/display-name.md why this is needed
SettingsView.displayName="SettingsView"